import { Add } from '@mui/icons-material';
import { Alert, Button, Skeleton, Typography } from '@mui/material';
import { useCommunityContentRestrictions } from 'admin-features/community/hooks';

import { LoadingOverlay } from 'shared/components/ui';
import { ContentRestrictionFragment } from 'shared/graphql';
import { useModalState } from 'shared/hooks';

import { ContentRestrictionListItem } from './ContentRestrictionListItem';
import { CreateContentRestrictionModal } from './CreateContentRestrictionModal';
import { EditContentRestrictionModal } from './EditContentRestrictionModal';
import {
  ContentRestrictionsList,
  Container,
  Content,
  Header,
  Section,
} from './SettingsCommunityContentRestrictions.styles';

const SKELETON_COUNT = 3;

/**
 * Setup the community content restrictions settings for the community.
 * @ticket https://github.com/jebelapp/jebel/issues/1842
 */
export function SettingsCommunityContentRestrictions() {
  const { data: restrictions, loading: isContentRestrictionsLoading } =
    useCommunityContentRestrictions();

  const {
    show: showEditModal,
    params: editableRestriction,
    open: openEditModal,
    close: closeEditModal,
  } = useModalState<ContentRestrictionFragment>();

  const { show: showCreateModal, open: openCreateModal, close: closeCreateModal } = useModalState();

  const isLoading = isContentRestrictionsLoading;

  return (
    <Container>
      <Header>
        <Typography variant="h6">Content Restrictions</Typography>
      </Header>

      <Content>
        <Section>
          <Typography>
            Create a content restriction filter to control what content community members can view
            based on their affiliation(s).
          </Typography>

          <Alert severity="warning">
            <strong>WARNING</strong>: Avoid setting overlapping or conflicting restrictions, as this
            may unintentionally block some members from accessing all content.
          </Alert>

          <LoadingOverlay
            loading={isLoading}
            overlay={
              <ContentRestrictionsList>
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <Skeleton key={index} variant="rectangular" height={75} />
                ))}
              </ContentRestrictionsList>
            }
          >
            {restrictions.length > 0 ? (
              <ContentRestrictionsList>
                {restrictions.map(restriction => (
                  <ContentRestrictionListItem
                    key={restriction.id}
                    restriction={restriction}
                    onEdit={() => openEditModal(restriction)}
                  />
                ))}
              </ContentRestrictionsList>
            ) : (
              <Typography color="textSecondary" align="center">
                No content restrictions found, create one to get started.
              </Typography>
            )}
          </LoadingOverlay>

          <Button
            startIcon={<Add />}
            variant="outlined"
            color="primary"
            disabled={isLoading}
            onClick={openCreateModal}
          >
            Add Content Restriction Filter
          </Button>

          <CreateContentRestrictionModal isOpen={showCreateModal} onClose={closeCreateModal} />

          {Boolean(editableRestriction && showEditModal) && (
            <EditContentRestrictionModal
              selectedRestriction={editableRestriction!}
              isOpen={showEditModal}
              onClose={closeEditModal}
            />
          )}
        </Section>
      </Content>
    </Container>
  );
}
